import { useRef, useState, useCallback, useEffect } from "react";
import AudioPlayer from "@/components/ui/audio-player";

interface AudioUploadFieldProps {
	onFileSelect: (file: File | null) => void;
	disabled?: boolean;
	className?: string;
}

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/wave", "audio/x-wav", "audio/ogg", "audio/mp4", "audio/x-m4a", "video/mp4", "audio/aac", "audio/x-aac"];
const ALLOWED_EXTENSIONS = [".mp3", ".wav", ".ogg", ".m4a"];

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function validateFile(file: File): string | null {
	const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
	const isValidType = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(fileExtension);

	if (!isValidType) {
		return `Invalid file type. Please upload MP3, WAV, OGG, or M4A files.`;
	}

	if (file.size > MAX_FILE_SIZE_BYTES) {
		return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Your file is ${formatFileSize(file.size)}.`;
	}

	return null;
}

export default function AudioUploadField({
	onFileSelect,
	disabled = false,
	className = "",
}: AudioUploadFieldProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	const [error, setError] = useState<string | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [audioDuration, setAudioDuration] = useState<number | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);

	// Cleanup preview URL on unmount
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	const handleFileSelect = useCallback((file: File) => {
		const validationError = validateFile(file);
		if (validationError) {
			setError(validationError);
			onFileSelect(null);
			return;
		}

		setSelectedFile(file);
		setError(null);

		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);

		onFileSelect(file);
	}, [onFileSelect, previewUrl]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleClick = () => {
		if (disabled) return;
		inputRef.current?.click();
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		if (disabled) return;
		setIsDragOver(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
		if (disabled) return;

		const file = e.dataTransfer.files[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleRemove = () => {
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
		setSelectedFile(null);
		setPreviewUrl(null);
		setAudioDuration(null);
		setError(null);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
		onFileSelect(null);
	};

	const handleAudioLoadedMetadata = (duration: number) => {
		setAudioDuration(duration);
	};

	// Idle state - show upload zone
	if (!selectedFile) {
		return (
			<div className={className}>
				<input
					ref={inputRef}
					type="file"
					accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/x-m4a,video/mp4,audio/aac,.mp3,.wav,.ogg,.m4a"
					onChange={handleInputChange}
					className="hidden"
					disabled={disabled}
				/>
				<div
					onClick={handleClick}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer group ${
						isDragOver
							? "border-brand bg-brand/5"
							: disabled
								? "border-gray-200 bg-gray-50 cursor-not-allowed"
								: "border-gray-200 hover:border-brand/50"
					}`}
				>
					<svg
						className={`w-10 h-10 mx-auto transition-colors ${
							isDragOver ? "text-brand" : "text-gray-300 group-hover:text-brand/80"
						}`}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
						/>
					</svg>
					<p className="text-sm text-tertiary mt-2">
						{isDragOver ? "Drop audio here" : "Click to upload or drag and drop"}
					</p>
					<p className="text-xs text-gray-400 mt-1">MP3, WAV, OGG, M4A up to {MAX_FILE_SIZE_MB}MB</p>
				</div>
				{error && (
					<p className="text-xs text-red-500 mt-2">{error}</p>
				)}
			</div>
		);
	}

	// File selected - show preview with audio player
	return (
		<div className={className}>
			<div className="border border-gray-200 rounded-xl overflow-hidden">
				{/* Audio player */}
				{previewUrl && (
					<div className="p-4 bg-gray-50">
						<AudioPlayer src={previewUrl} onLoadedMetadata={handleAudioLoadedMetadata} />
					</div>
				)}

				<div className="p-4 border-t border-gray-200">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
								<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
									/>
								</svg>
							</div>
							<div>
								<p className="text-sm font-medium text-primary truncate max-w-[200px]">
									{selectedFile.name}
								</p>
								<p className="text-xs text-tertiary">
									{formatFileSize(selectedFile.size)}
									{audioDuration && ` · ${formatDuration(audioDuration)}`}
								</p>
							</div>
						</div>

						<button
							type="button"
							onClick={handleRemove}
							disabled={disabled}
							className="p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
							title="Remove audio"
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
